import { useAlertsStore } from '../store/alerts';

/**
 * Wraps a sub_id with a subtle cyan ring-pulse when that sub_id has just
 * received a conversion. Companion piece to LightningStrikeOverlay (which
 * draws the 4 corner brackets at the same target).
 *
 * Replaces the old "magenta border + yellow lightning icon" treatment —
 * that was visually loud and clashed with the cyberpunk HUD aesthetic.
 * The new effect is one-shot (single ring expands + fades, ~600ms),
 * not a continuous pulse, so the row doesn't keep blinking after the
 * notification has been acknowledged.
 *
 * Usage:
 *   <SubIdLightning subId={row.subId}>{row.subId}</SubIdLightning>
 *
 * The `data-flash-sub` attribute is always set (even when not flashing)
 * so LightningStrikeOverlay can locate this DOM node the moment a flash
 * is enqueued — overlay queries DOM in the same React tick.
 */
export function SubIdLightning({
  subId,
  children,
  className = '',
}: {
  subId: string | null | undefined;
  children: React.ReactNode;
  className?: string;
}) {
  const recent = useAlertsStore((s) => s.recentlyConverted);
  const flashing =
    !!subId &&
    recent.some((f) => f.subId === subId && f.expiresAt > Date.now());

  // Marker for the overlay's bbox query — kept regardless of flashing state.
  if (!flashing) {
    return (
      <span className={className} data-flash-sub={subId ?? undefined}>
        {children}
      </span>
    );
  }

  // Ring-pulse one-shot. Animation timing matches the overlay's
  // bracket-lock so the visual cue feels coordinated.
  return (
    <span
      data-flash-sub={subId ?? undefined}
      className={`relative inline-block px-1 -mx-1 rounded-sm ${className}`}
      style={{
        animation: 'target-ring-pulse 0.6s ease-out forwards',
      }}
    >
      {children}
    </span>
  );
}
